import { format, addDays, differenceInCalendarDays, isValid, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

export const MAX_RANGE_DAYS = 45;

export const END_LABELS = {
  origin: 'Departure',
  destination: 'Arrival',
  airline: 'Airline',
};

// Keep the ids in step with REJECT_REASONS in prm-bookings/routes/pax-desk.js.
export const REJECT_REASONS = [
  { id: 'duplicate', label: 'Duplicate request' },
  { id: 'passenger-cancelled', label: 'Passenger cancelled' },
  { id: 'flight-cancelled', label: 'Flight cancelled or rescheduled' },
  { id: 'no-contact', label: 'Unable to contact the passenger' },
  { id: 'not-eligible', label: 'Assistance not required' },
  { id: 'arranged-directly', label: 'Already arranged with the airport or airline' },
  { id: 'incorrect-details', label: 'Flight or booking details are wrong' },
];

// 'YYYY-MM-DD' for a local Date
export const toYmd = (date) => format(date, 'yyyy-MM-dd');

export const defaultRange = () => {
  const today = new Date();
  return { from: toYmd(addDays(today, -1)), to: toYmd(addDays(today, 30)) };
};

// Calendar ranges for the desk date shortcuts. Week starts Monday.
export const presetRange = (preset) => {
  const today = new Date();
  if (preset === 'today') return { from: toYmd(today), to: toYmd(today) };
  if (preset === 'week') {
    return {
      from: toYmd(startOfWeek(today, { weekStartsOn: 1 })),
      to: toYmd(endOfWeek(today, { weekStartsOn: 1 })),
    };
  }
  return { from: toYmd(startOfMonth(today)), to: toYmd(endOfMonth(today)) };
};

export const activePreset = (range) => {
  if (!range) return null;
  for (const preset of ['today', 'week', 'month']) {
    const next = presetRange(preset);
    if (next.from === range.from && next.to === range.to) return preset;
  }
  return null;
};

// parse 'YYYY-MM-DD' as a local calendar date (no timezone shift)
const parseYmd = (value) => {
  if (typeof value !== 'string') return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isValid(d) ? d : null;
};

// returns an error message, or null when the range is OK
export const validateRange = (from, to) => {
  const f = parseYmd(from);
  const t = parseYmd(to);
  if (!f || !t) return 'Choose a start and end date.';
  if (t < f) return 'The end date must be on or after the start date.';
  if (differenceInCalendarDays(t, f) > MAX_RANGE_DAYS) return `The date range can be at most ${MAX_RANGE_DAYS} days.`;
  return null;
};

// '2026-09-31' or an ISO date-time -> '31-09-26'
export const formatShortDate = (value) => {
  if (!value || typeof value !== 'string') return '';
  const day = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) return `${day[3]}-${day[2]}-${day[1].slice(2)}`;
  const d = parseISO(value);
  return isValid(d) ? format(d, 'dd-MM-yy') : '';
};

// 19:30 -> '19:30pm', 10:00 -> '10:00am'. Keeps the 24-hour clock and adds am/pm.
const clockWithMeridiem = (hours, minutes) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0 || h > 23) return '';
  const m = Number(minutes);
  if (!Number.isFinite(m) || m < 0 || m > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
};

// Date plus clock in the same value: '23-10-26 19:30pm'.
// Pass a separate clock string for schedule times (STD). A date with no time stays date-only.
export const formatShortDateTime = (value, timeValue) => {
  const date = formatShortDate(value);
  if (!date) return '';
  let clock = '';
  if (typeof timeValue === 'string') {
    const hm = timeValue.match(/(\d{1,2}):(\d{2})/);
    if (hm) clock = clockWithMeridiem(hm[1], hm[2]);
  } else if (typeof value === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = parseISO(value);
    if (isValid(d)) clock = clockWithMeridiem(d.getHours(), d.getMinutes());
  }
  return clock ? `${date} ${clock}` : date;
};

// '2026-10-13' -> '13 Oct 2026'
export const formatDay = (value) => {
  const d = parseYmd(value);
  return d ? format(d, 'd MMM yyyy') : (value ? String(value) : '');
};

// '07:05', '07:05:00' or an ISO date-time -> '07:05'
export const formatTime = (value) => {
  if (!value || typeof value !== 'string') return '';
  const hm = value.match(/^(\d{1,2}):(\d{2})/);
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2]}`;
  const d = parseISO(value);
  return isValid(d) ? format(d, 'HH:mm') : value;
};

// ISO date-time -> '13 Oct 14:03' (or with year when asked)
export const formatDateTime = (value, withYear = false) => {
  if (!value) return '';
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  if (!isValid(d)) return String(value);
  return format(d, withYear ? 'd MMM yyyy HH:mm' : 'd MMM HH:mm');
};

// 'Dublin' -> 'Dublin Airport'; leaves 'Dublin Airport' alone
export const airportName = (name) => {
  if (!name) return '';
  return /airport/i.test(name) ? name : `${name} Airport`;
};

// 'FR 123 · 13 Oct 2026 · 07:05–08:10'
export const formatFlight = (flight) => {
  if (!flight) return '';
  const code = [flight.carrierCode, flight.flightNumber].filter(Boolean).join(' ');
  const dep = formatTime(flight.departureTime);
  const arr = formatTime(flight.arrivalTime);
  const times = dep && arr ? `${dep}–${arr}` : dep || arr;
  return [code, formatDay(flight.departureDate), times].filter(Boolean).join(' · ');
};

// who to credit when orgLabel is missing
export const confirmationLabel = (confirmation, request, end) => {
  if (confirmation?.orgLabel) return confirmation.orgLabel;
  switch (confirmation?.source) {
    case 'DESK':
      return 'PRM Assist';
    case 'AIRLINE':
      return request?.flight?.airlineName || 'the airline';
    case 'AIRPORT':
    case 'TIMELINE':
      return end === 'destination' ? request?.destination?.iata || 'the airport' : request?.origin?.iata || 'the airport';
    default:
      return '';
  }
};

// text for the confirm dialog
export const confirmQuestion = (request, end) => {
  const name = request?.name || 'this passenger';
  if (end === 'airline') {
    const airline = request?.flight?.airlineName || 'the airline';
    return `Confirm the airline assistance reservation with ${airline} for ${name}?`;
  }
  const airport = end === 'origin' ? request?.origin : request?.destination;
  const kind = end === 'origin' ? 'departure' : 'arrival';
  const where = [airportName(airport?.name), airport?.iata ? `(${airport.iata})` : ''].filter(Boolean).join(' ');
  return `Confirm ${kind} assistance at ${where || 'the airport'} for ${name}?`;
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const pad2 = (n) => String(n).padStart(2, '0');

// Confirm within 48 hours of the request being created.
export const createdSlaTone = (elapsedMs) => {
  if (elapsedMs < 24 * HOUR_MS) return 'green';
  if (elapsedMs < 36 * HOUR_MS) return 'yellow';
  if (elapsedMs < 48 * HOUR_MS) return 'orange';
  return 'red';
};

// Book at least 48 hours before departure. Closer than that steps through the same bands.
export const departureSlaTone = (remainingMs) => {
  if (remainingMs >= 48 * HOUR_MS) return 'green';
  if (remainingMs >= 36 * HOUR_MS) return 'yellow';
  if (remainingMs >= 24 * HOUR_MS) return 'orange';
  return 'red';
};

export const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  return `${hours}h ${pad2(minutes)}m ${pad2(seconds)}s`;
};

// Days until the last 3 days, then an hour countdown.
export const formatUntilDeparture = (ms) => {
  if (ms < 0) return 'Departed';
  if (ms > 3 * DAY_MS) return `${Math.floor(ms / DAY_MS)}d`;
  return formatElapsed(ms);
};

export const createdAtMs = (value) => {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d.getTime() : null;
};

// Schedule date + clock time, read as the desk's local time.
export const scheduledDepartureMs = (flight) => {
  if (!flight || typeof flight.departureDate !== 'string') return null;
  const day = flight.departureDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!day) return null;
  let hours = 0;
  let minutes = 0;
  if (typeof flight.departureTime === 'string') {
    const hm = flight.departureTime.match(/(\d{1,2}):(\d{2})/);
    if (hm) {
      hours = Number(hm[1]);
      minutes = Number(hm[2]);
    }
  }
  const date = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), hours, minutes, 0, 0);
  return isValid(date) ? date.getTime() : null;
};
